/* @flow */
import { observable, action, computed, runInAction } from 'mobx';

import * as defaultApi from 'api/brandsBulkImport';
import { isImportFinished } from 'api/brandsBulkImport';
import type {
  MultipleBrandUploadPayload,
  MultipleBrandPreviewResult,
  MultipleBrandPreviewJobStatus,
  MultipleBrandJobStatus,
} from 'api/brandsBulkImport';

import TimeoutPoller from './TimeoutPoller';

export type UploadPhase = 'upload' | 'previewing' | 'preview' | 'processing' | 'result' | 'error';

export type MultipleBrandUploadApi = {
  previewMultipleBrandsUpload: (
    payload: MultipleBrandUploadPayload
  ) => Promise<{
    previewId: string,
  }>,
  getMultipleBrandsPreviewStatus: (previewId: string) => Promise<MultipleBrandPreviewJobStatus>,
  confirmMultipleBrandsUpload: (payload: MultipleBrandUploadPayload) => Promise<{ jobId: string }>,
  getMultipleBrandsUploadStatus: (jobId: string) => Promise<MultipleBrandJobStatus>,
};

const DEFAULT_POLL_INTERVAL_MS = 5000;

// Drives the upload -> preview -> confirm -> processing -> result flow for the
// "Import Brands / Trading Accounts" form. Both the preview and the confirmed
// import are processed asynchronously on the backend, so this controller
// polls a status endpoint for each of them via `TimeoutPoller`. The API and
// timer functions are injectable so this class can be unit tested without
// real network calls or waiting on real timers.
export default class MultipleBrandUploadController {
  api: MultipleBrandUploadApi;

  pollIntervalMs: number;

  setTimeoutFn: (fn: () => void, ms: number) => *;

  clearTimeoutFn: (id: *) => void;

  previewPoller: ?TimeoutPoller<MultipleBrandPreviewJobStatus> = null;

  importPoller: ?TimeoutPoller<MultipleBrandJobStatus> = null;

  @observable phase: UploadPhase = 'upload';

  @observable isLoading: boolean = false;

  @observable errorMessage: ?string = null;

  @observable payload: MultipleBrandUploadPayload = { brandsFile: null, tradingAccountsFile: null };

  @observable previewResult: ?MultipleBrandPreviewResult = null;

  @observable jobStatus: ?MultipleBrandJobStatus = null;

  constructor(
    api: MultipleBrandUploadApi = defaultApi,
    {
      pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
      setTimeoutFn = (fn: () => void, ms: number) => setTimeout(fn, ms),
      clearTimeoutFn = (id: *) => clearTimeout(id),
    }: {
      pollIntervalMs?: number,
      setTimeoutFn?: (fn: () => void, ms: number) => *,
      clearTimeoutFn?: (id: *) => void,
    } = {}
  ) {
    this.api = api;
    this.pollIntervalMs = pollIntervalMs;
    this.setTimeoutFn = setTimeoutFn;
    this.clearTimeoutFn = clearTimeoutFn;
  }

  @computed
  get hasAtLeastOneFile(): boolean {
    return Boolean(this.payload.brandsFile || this.payload.tradingAccountsFile);
  }

  @action
  requestPreview(payload: MultipleBrandUploadPayload): Promise<void> {
    // Re-entrancy guard: ignore a second call (e.g. a double-click, or
    // Enter + click racing each other) while a preview request is in flight.
    if (this.isLoading) {
      return Promise.resolve();
    }

    this.payload = payload;
    this.isLoading = true;
    this.errorMessage = null;
    // Move to the waiting screen immediately, before the request that queues
    // the preview job even resolves: on a slow connection the "Preview"
    // button just sitting there disabled (with no other feedback) looks
    // like nothing is happening, even though the request is still in flight.
    this.phase = 'previewing';
    this.previewResult = null;

    return this.api
      .previewMultipleBrandsUpload(payload)
      .then(({ previewId }) => {
        runInAction(() => {
          this.isLoading = false;
          this.startPreviewPolling(previewId);
        });
      })
      .catch(err => {
        runInAction(() => {
          this.errorMessage = (err && err.message) || 'Unable to preview the uploaded files.';
          this.isLoading = false;
          this.phase = 'upload';
        });
      });
  }

  @action
  startPreviewPolling(previewId: string): void {
    this.stopPreviewPolling();
    this.errorMessage = null;

    this.previewPoller = new TimeoutPoller({
      fetchStatus: () => this.api.getMultipleBrandsPreviewStatus(previewId),
      isFinished: status => isImportFinished(status.status),
      intervalMs: this.pollIntervalMs,
      setTimeoutFn: this.setTimeoutFn,
      clearTimeoutFn: this.clearTimeoutFn,
      onUpdate: status => this.handlePreviewStatus(status),
      onError: err => {
        runInAction(() => {
          this.errorMessage = (err && err.message) || 'Unable to check the preview status.';
        });
      },
    });
    this.previewPoller.start();
  }

  @action
  handlePreviewStatus(status: MultipleBrandPreviewJobStatus): void {
    runInAction(() => {
      this.errorMessage = null;

      if (!isImportFinished(status.status)) {
        return;
      }

      this.stopPreviewPolling();

      if (status.status === 'failed') {
        this.errorMessage = status.failureMessage || 'Unable to generate the import preview.';
        this.phase = 'upload';
        return;
      }

      this.previewResult = {
        brands: status.brands,
        tradingAccounts: status.tradingAccounts,
      };
      this.phase = 'preview';
    });
  }

  @action
  stopPreviewPolling(): void {
    if (this.previewPoller) {
      this.previewPoller.stop();
      this.previewPoller = null;
    }
  }

  @action
  backToUpload(): void {
    this.phase = 'upload';
    this.previewResult = null;
    this.errorMessage = null;
  }

  @action
  confirmUpload(): Promise<void> {
    // Re-entrancy guard: this call queues a real backend import job, so it's
    // important a double-click (or any other re-entrant call) can't queue it twice.
    if (this.isLoading) {
      return Promise.resolve();
    }

    this.isLoading = true;
    this.errorMessage = null;
    // Same reasoning as `requestPreview`: show the waiting screen right
    // away rather than leaving the user on the preview screen wondering
    // whether the "Confirm" click actually did anything.
    this.phase = 'processing';
    this.jobStatus = null;

    return this.api
      .confirmMultipleBrandsUpload(this.payload)
      .then(({ jobId }) => {
        runInAction(() => {
          this.isLoading = false;
          this.startImportPolling(jobId);
        });
      })
      .catch(err => {
        runInAction(() => {
          this.errorMessage = (err && err.message) || 'Unable to submit the uploaded files.';
          this.isLoading = false;
          this.phase = 'preview';
        });
      });
  }

  @action
  startImportPolling(jobId: string): void {
    this.stopImportPolling();
    this.errorMessage = null;

    this.importPoller = new TimeoutPoller({
      fetchStatus: () => this.api.getMultipleBrandsUploadStatus(jobId),
      isFinished: status => isImportFinished(status.status),
      intervalMs: this.pollIntervalMs,
      setTimeoutFn: this.setTimeoutFn,
      clearTimeoutFn: this.clearTimeoutFn,
      onUpdate: status => this.handleImportStatus(status),
      onError: err => {
        runInAction(() => {
          this.errorMessage = (err && err.message) || 'Unable to check the import status.';
        });
      },
    });
    this.importPoller.start();
  }

  @action
  handleImportStatus(status: MultipleBrandJobStatus): void {
    runInAction(() => {
      this.jobStatus = status;
      this.errorMessage = null;

      if (isImportFinished(status.status)) {
        this.stopImportPolling();
        this.phase = 'result';
      }
    });
  }

  @action
  stopImportPolling(): void {
    if (this.importPoller) {
      this.importPoller.stop();
      this.importPoller = null;
    }
  }

  @action
  dispose(): void {
    this.stopPreviewPolling();
    this.stopImportPolling();
  }
}
